import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import type { Order, PaymentResult } from '../api/types';
import {
  Alert,
  EmptyState,
  Spinner,
  formatDate,
  money,
  statusLabel,
  statusTone,
} from '../components/common';

export function OrdersPage() {
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .myOrders()
      .then(setOrders)
      .catch((e: Error) => setError(e.message));
  }, []);

  if (error) return <Alert>{error}</Alert>;
  if (!orders) return <Spinner label="Loading your orders…" />;

  if (orders.length === 0) {
    return (
      <EmptyState
        title="No orders yet"
        message="Once you place an order it will show up here, with its live status."
        action={
          <Link to="/" className="btn btn--primary">
            Browse the menu
          </Link>
        }
      />
    );
  }

  return (
    <section className="page">
      <h1>My orders</h1>

      <ul className="orders">
        {orders.map((order) => (
          <li key={order.id}>
            <Link to={`/orders/${order.id}`} className="orderrow">
              <div>
                <strong>{order.orderNumber}</strong>
                <span className="muted small"> · {formatDate(order.placedAt)}</span>
                <p className="muted small">
                  {order.items.reduce((n, i) => n + i.quantity, 0)} item(s) · {order.fulfilment}
                </p>
              </div>
              <div className="orderrow__right">
                <span className={`pill pill--${statusTone(order.status)}`}>{statusLabel(order.status)}</span>
                <strong>{money(order.total)}</strong>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) return;

    api
      .order(orderId)
      .then(setOrder)
      .catch((e: Error) => setError(e.message));
  }, [orderId]);

  if (error) return <Alert>{error}</Alert>;
  if (!order) return <Spinner label="Loading your order…" />;

  return (
    <section className="page page--narrow">
      <header className="page__head">
        <div>
          <h1>{order.orderNumber}</h1>
          <p className="muted small">Placed {formatDate(order.placedAt)}</p>
        </div>
        <span className={`pill pill--${statusTone(order.status)}`}>{statusLabel(order.status)}</span>
      </header>

      <ul className="receipt">
        {order.items.map((item) => (
          <li key={item.id}>
            <div>
              <strong>
                {item.quantity} × {item.productName}
              </strong>{' '}
              <span className="muted">({item.variantLabel})</span>
              {item.addOns.length > 0 && <p className="muted small">+ {item.addOns.join(', ')}</p>}
              {item.notes && <p className="muted small italic">“{item.notes}”</p>}
            </div>
            <strong>{money(item.lineTotal)}</strong>
          </li>
        ))}
      </ul>

      <div className="summary">
        <div className="summary__row">
          <span>Subtotal</span>
          <strong>{money(order.subtotal)}</strong>
        </div>
        <div className="summary__row">
          <span>{order.fulfilment === 'Delivery' ? 'Delivery' : 'Collection'}</span>
          <strong>{order.deliveryFee === 0 ? 'FREE' : money(order.deliveryFee)}</strong>
        </div>
        <div className="summary__row summary__row--total">
          <span>Total</span>
          <strong>{money(order.total)}</strong>
        </div>
      </div>

      {order.delivery && (
        <div className="panel">
          <h2 className="panel__title">Delivering to</h2>
          <p>
            <strong>{order.delivery.recipientName}</strong> · {order.delivery.phone}
          </p>
          <p>{order.delivery.address}</p>
          {order.delivery.instructions && <p className="muted small">{order.delivery.instructions}</p>}
        </div>
      )}

      {order.notes && (
        <div className="panel">
          <h2 className="panel__title">Your note</h2>
          <p>{order.notes}</p>
        </div>
      )}

      <Link to="/orders" className="btn btn--ghost btn--block">
        Back to my orders
      </Link>
    </section>
  );
}

/**
 * Where Paystack sends the customer after payment. The redirect is only a hint —
 * the reference is verified server-side before anything is called paid.
 */
export function PaymentCallbackPage() {
  const [params] = useSearchParams();
  const reference = params.get('reference') ?? params.get('trxref');

  const [result, setResult] = useState<PaymentResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!reference) {
      setError('No payment reference came back from the payment provider.');
      return;
    }

    let cancelled = false;
    let timer = 0;

    // Yoco confirms by webhook, not by this redirect, so the answer can arrive
    // a second or two after the customer is already back here. Poll for a
    // short while before giving up rather than telling them it failed.
    const MAX_ATTEMPTS = 8;

    async function check(round: number) {
      try {
        const response = await api.verifyPayment(reference!);

        if (cancelled) return;

        if (response.isPaid || round >= MAX_ATTEMPTS) {
          setResult(response);
          sessionStorage.removeItem('es.pendingOrderId');
          return;
        }

        setAttempt(round + 1);
        timer = window.setTimeout(() => void check(round + 1), 2000);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    }

    void check(0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [reference]);

  if (error) {
    return (
      <section className="page page--narrow center">
        <Alert>{error}</Alert>
        <Link to="/orders" className="btn btn--ghost">
          Check my orders
        </Link>
      </section>
    );
  }

  if (!result) {
    return (
      <Spinner
        label={attempt > 2 ? 'Still confirming your payment…' : 'Confirming your payment…'}
      />
    );
  }

  if (!result.isPaid) {
    return (
      <section className="page page--narrow center">
        <h1>Payment not confirmed yet</h1>
        <Alert>{result.message ?? 'The payment did not go through.'}</Alert>
        <p className="muted">
          Order {result.orderNumber} is still waiting. If your bank has already taken the money it
          will confirm shortly — check your orders in a minute before trying again.
        </p>
        <Link to="/orders" className="btn btn--primary">
          My orders
        </Link>
      </section>
    );
  }

  return (
    <section className="page page--narrow center">
      <div className="success">✓</div>
      <h1>Thanks — we&apos;ve got your order!</h1>
      <p className="muted">
        Order <strong>{result.orderNumber}</strong> is paid and on its way to the kitchen. A
        confirmation email is on its way to you.
      </p>

      <Link to={`/orders/${result.orderId}`} className="btn btn--primary">
        Track this order
      </Link>
      <Link to="/" className="btn btn--ghost">
        Order something else
      </Link>
    </section>
  );
}
